import React, { useEffect, useState } from "react";
import axios from "axios";
import ResultCardAttribute from "./ResultCardAttribute";
import { Box } from "@mui/material";
import SummaryCard from "./SummaryCard";
import Loader from "./Loader";
import UploadDownloadCard from "./UploadDownloadCard";
import GlassSnackbar from "./GlassSnackbar"; // ✅ reusable snackbar

// Updated categories with attribute names
const sections = {
  "🏢 Company Overview": [
    "Company legal name",
    "Headquarters address",
    "Established Date",
    "Private or Public",
  ],
  "🏗️ Company Structure": [
    "Organization type",
    "Industry",
    "Entity structure",
    "Tax classification",
  ],
  "💵 Financials": [
    "Tax ID",
    "Gross annual revenue",
    "Net annual revenue",
    "Operating revenue",
  ],
  "🌐 Operations": [
    "Critical contacts",
    "Number of employees",
    "Geographic distribution",
  ],
  "📈 Stock Information": [
    "Market position",
    "Ticker symbol",
    "Stock exchange where traded",
  ],
  "🛡️ Classification & Compliance": ["NAICS", "ESG tag", "OFAC List"],
};

const extractLatestNews = (attributes) => {
  return attributes.find((attr) => attr.name === "Latest News");
};

const ProfileView = ({ org }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [news, setNews] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryId, setSummaryId] = useState(null);
    const [summarySource, setSummarySource] = useState(null);
      const [summaryDomain, setSummaryDomain] = useState(null);
  const [sessionID, setSessionID] = useState(null);
  

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const fetchProfile = async () => {
    if (!org?.name || !org?.address) return;

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/company-profile`,
        {
          params: {
            company_name: org.name,
            address: org.address,
          },
        }
      );
      setData(res.data);
      setNews(extractLatestNews(res.data?.attributes));
      setSummary(res.data.attributes.find((a) => a.name === "Summary")?.value);
      setSummaryId(res.data.attributes.find((a) => a.name === "Summary")?.id);
      setSummaryDomain(res.data.attributes.find((a) => a.name === "Summary")?.domain);
       setSummarySource(res.data.attributes.find((a) => a.name === "Summary")?.source);
      setSessionID(res.data.session_id);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [org]);

  const onSummaryStatusChange=(newStatus)=>{
    

    handleStatusChange(summaryId,newStatus);
  }

  const handleStatusChange = async (attrId, newStatus) => {
    if (!data?.attributes) return;

    const updatedData = {
      ...data,
      attributes: data.attributes.map((attr) =>
        attr.id === attrId ? { ...attr, status: newStatus } : attr
      ),
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/update-company-status`,
        updatedData
      );
      console.log("Update response:", res.data);
      setData(updatedData);

      setSnackbarSeverity("success");
      setSnackbarMessage("Success: Record updated successfully in Reltio.");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to update status:", err);
      const message =
        err?.response?.data?.error || "Failed to update status. Please try again.";
      setSnackbarSeverity("error");
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    }
  };

  const groupAttributes = () => {
    if (!data?.attributes) return [];

    return Object.entries(sections)
      .map(([title, fieldNames]) => {
        const attributes = fieldNames
          .map((name) => data.attributes.find((attr) => attr.name === name))
          .filter(Boolean)
          .map((attr) => ({
            id: attr.id,
            label: attr.name,
            value: attr.value,
            source: attr.source,
            status: attr.status,
            domain: attr.domain,
          }));

        return attributes.length > 0 ? { title, attributes } : null;
      })
      .filter(Boolean);
  };

  if (loading) {
    return (
      <Box sx={{ mt: "-2rem", ml: "-15rem", width: "40rem" }}>
        <Loader />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pl: 0,
          pr:4,
          gap: 4,
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Left Column - 70% */}
        <Box sx={{ flex: "0 0 70%", mt: -3 }}>
          {groupAttributes().map((section) => (
            <ResultCardAttribute
              key={section.title}
              title={section.title}
              attributes={section.attributes}
              onStatusChange={handleStatusChange}
            />
          ))}
        </Box>

        {/* Right Column - 30% */}
        <Box sx={{ flex: "0 0 30%" }}>
          <UploadDownloadCard
            sessionID={sessionID}
            onStatusUpdate={(message, severity) => {
              setSnackbarMessage(message);
              setSnackbarSeverity(severity);
              setSnackbarOpen(true);
            }}
          />
          <SummaryCard
            data={{ sdf: "", description: summary }}
            news={news}
            org={org}
             onSummaryStatusChange={onSummaryStatusChange}
             source={summarySource}
             domain={summaryDomain}
          />
        </Box>
      </Box>

      {/* Snackbar */}
      <GlassSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default ProfileView;
